/**
 * ResourceManager (offline) — identyczna logika jak backend, bez Mongoose
 */

// Stałe poza klasą (getter zamiast `static X = …`): Next.js kompiluje pola statyczne do bloku
// `static {}`, którego nie zna Chrome 92 w Chromecaście / Google TV.
const STATIC_CITY_ZONE = {
  mercenary_camp: {
    id: 'city_mercenary_camp',
    name: 'Obóz Najemników',
    cost: { gold: 2 },
    gain: { strength: 1 },
    choice: ['strength', 'knowledge'],
  },
  small_favors: {
    id: 'city_small_favors',
    name: 'Drobne Przysługi',
    cost: { knowledge: 2 },
    gain: { gold: 2 },
  },
  rat_plague: {
    id: 'city_rat_plague',
    name: 'Szczurza Plaga',
    cost: { strength: 2 },
    gain: { gold: 2 },
  },
};
const STATIC_SKARBIEC_LIMIT = 10;

export class ResourceManager {
  static get CITY_ZONE() { return STATIC_CITY_ZONE; }

  static get SKARBIEC_LIMIT() { return STATIC_SKARBIEC_LIMIT; }

  static getBankLimit(game) {
    return game?.gameConfig?.bankResourceLimit || this.SKARBIEC_LIMIT;
  }

  static ensurePool(player) {
    if (!player.pool || typeof player.pool !== 'object') {
      player.pool = { strength: 0, knowledge: 0, gold: 0 };
    }
    if (typeof player.pool.strength !== 'number') player.pool.strength = 0;
    if (typeof player.pool.knowledge !== 'number') player.pool.knowledge = 0;
    if (typeof player.pool.gold !== 'number') player.pool.gold = 0;
  }

  static addToPool(player, resources) {
    this.ensurePool(player);
    const added = {};
    if (resources.strength) { player.pool.strength += resources.strength; added.strength = resources.strength; }
    if (resources.knowledge) { player.pool.knowledge += resources.knowledge; added.knowledge = resources.knowledge; }
    if (resources.gold) { player.pool.gold += resources.gold; added.gold = resources.gold; }
    return added;
  }

  static removeFromPool(player, resources) {
    this.ensurePool(player);
    const removed = {};
    if (resources.strength) { const r = Math.min(player.pool.strength, resources.strength); player.pool.strength -= r; removed.strength = r; }
    if (resources.knowledge) { const r = Math.min(player.pool.knowledge, resources.knowledge); player.pool.knowledge -= r; removed.knowledge = r; }
    if (resources.gold) { const r = Math.min(player.pool.gold, resources.gold); player.pool.gold -= r; removed.gold = r; }
    return removed;
  }

  static resetPool(player) {
    this.ensurePool(player);
    const lost = { ...player.pool };
    player.pool.strength = 0;
    player.pool.knowledge = 0;
    player.pool.gold = 0;
    return lost;
  }

  static transferToBank(player, resources, game = null) {
    this.ensurePool(player);
    const bankLimit = this.getBankLimit(game);
    const transferred = {};
    const overflow = {};
    for (const res of ['strength', 'knowledge', 'gold']) {
      if (resources[res] && resources[res] > 0) {
        const fromPool = Math.min(resources[res], player.pool[res]);
        const space = bankLimit - (player.resources[res] || 0);
        const toBank = Math.min(fromPool, space);
        player.pool[res] -= fromPool;
        player.resources[res] = (player.resources[res] || 0) + toBank;
        transferred[res] = toBank;
        overflow[res] = fromPool - toBank;
      }
    }
    return { transferred, overflow };
  }

  static transferAllToBank(player, game = null) {
    this.ensurePool(player);
    return this.transferToBank(player, { ...player.pool }, game);
  }

  /**
   * Zabierz zasoby z PULI, potem z BANKU — ile się da, bez schodzenia poniżej zera.
   * Narzędzie do Ofiar: „stracisz 3 Złota" przy 1 Złocie znaczy „zostajesz z zerem".
   * Lustro `backend/services/ResourceManager.drainFromPoolAndBank`.
   */
  static drainFromPoolAndBank(player, amounts) {
    this.ensurePool(player);
    if (!player.resources) player.resources = { strength: 0, knowledge: 0, gold: 0 };
    const drained = {};
    for (const resource of ['strength', 'knowledge', 'gold']) {
      const wanted = amounts?.[resource] || 0;
      if (wanted <= 0) continue;
      const fromPool = Math.min(wanted, player.pool[resource] || 0);
      player.pool[resource] = (player.pool[resource] || 0) - fromPool;
      const fromBank = Math.min(wanted - fromPool, player.resources[resource] || 0);
      player.resources[resource] = (player.resources[resource] || 0) - fromBank;
      drained[resource] = fromPool + fromBank;
    }
    return drained;
  }

  static canAffordFromPoolAndBank(player, cost) {
    this.ensurePool(player);
    const pool = player.pool;
    const bank = player.resources;
    if (cost.strength && (pool.strength + (bank.strength || 0)) < cost.strength) return false;
    if (cost.knowledge && (pool.knowledge + (bank.knowledge || 0)) < cost.knowledge) return false;
    if (cost.gold && (pool.gold + (bank.gold || 0)) < cost.gold) return false;
    return true;
  }

  static spendFromPoolOrBank(player, cost) {
    if (!this.canAffordFromPoolAndBank(player, cost)) {
      return { success: false, error: 'Niewystarczające zasoby' };
    }
    this.ensurePool(player);
    const spentFromPool = {};
    const spentFromBank = {};
    for (const res of ['strength', 'knowledge', 'gold']) {
      if (cost[res] && cost[res] > 0) {
        let remaining = cost[res];
        const fromPool = Math.min(remaining, player.pool[res]);
        player.pool[res] -= fromPool;
        spentFromPool[res] = fromPool;
        remaining -= fromPool;
        if (remaining > 0) {
          player.resources[res] -= remaining;
          spentFromBank[res] = remaining;
        }
      }
    }
    return { success: true, spentFromPool, spentFromBank };
  }

  static addResources(player, resources, game = null) {
    const bankLimit = this.getBankLimit(game);
    const added = {};
    if (resources.strength) {
      const before = player.resources.strength || 0;
      player.resources.strength = Math.min(before + resources.strength, bankLimit);
      added.strength = player.resources.strength - before;
    }
    if (resources.knowledge) {
      const before = player.resources.knowledge || 0;
      player.resources.knowledge = Math.min(before + resources.knowledge, bankLimit);
      added.knowledge = player.resources.knowledge - before;
    }
    if (resources.gold) {
      const before = player.resources.gold || 0;
      player.resources.gold = Math.min(before + resources.gold, bankLimit);
      added.gold = player.resources.gold - before;
    }
    return added;
  }

  static canAfford(player, cost) {
    if (cost.strength && (player.resources.strength || 0) < cost.strength) return false;
    if (cost.knowledge && (player.resources.knowledge || 0) < cost.knowledge) return false;
    if (cost.gold && (player.resources.gold || 0) < cost.gold) return false;
    return true;
  }

  static spendResources(player, cost) {
    if (!this.canAfford(player, cost)) throw new Error('Niewystarczające zasoby');
    if (cost.strength) player.resources.strength -= cost.strength;
    if (cost.knowledge) player.resources.knowledge -= cost.knowledge;
    if (cost.gold) player.resources.gold -= cost.gold;
  }

  static applySkarbiecLimit(player, game = null) {
    const bankLimit = this.getBankLimit(game);
    const lost = { strength: 0, knowledge: 0, gold: 0 };
    for (const res of ['strength', 'knowledge', 'gold']) {
      if ((player.resources[res] || 0) > bankLimit) {
        lost[res] = player.resources[res] - bankLimit;
        player.resources[res] = bankLimit;
      }
    }
    return lost;
  }
}

export default ResourceManager;
